import { computed, unref } from "vue";
import { useUserCardCacheStore } from "@/entities/user/model/userCardCache.store";

/**
 * Подписка на конкретного user'а в кеше + загрузка/отмена.
 *
 * Внутри computed-ы изолируют рендер: ререндерится только тот card,
 * чья entry изменилась, остальные не реагируют.
 *
 * @param {string | import('vue').Ref<string> | (() => string)} userId
 */
export function useUserCard(userId) {
  const cache = useUserCardCacheStore();

  const id = computed(() =>
    typeof userId === "function" ? userId() : unref(userId)
  );

  const entry = computed(() => {
    // Подписываемся на triggerRef через .value
    const map = cache.entriesRef;
    void map.value;
    return cache.get(id.value);
  });

  const data = computed(() => entry.value?.data ?? null);
  const status = computed(() => entry.value?.status ?? "idle");
  const error = computed(() => entry.value?.error ?? null);
  const isLoading = computed(() => status.value === "loading" && !data.value);
  const isRevalidating = computed(
    () => status.value === "loading" && !!data.value
  );
  const isError = computed(() => status.value === "error");

  function load() {
    if (!id.value) return Promise.resolve(null);
    // Ошибки доступны через реактивный error/isError из cache entry,
    // поэтому промис не должен реджектиться — иначе unhandled rejection
    // при cancel() (быстрое закрытие hover-card).
    return cache.fetchUser(id.value).catch(() => null);
  }

  function cancel() {
    if (id.value) cache.cancel(id.value);
  }

  function invalidate() {
    if (id.value) cache.invalidate(id.value);
  }

  return {
    data,
    status,
    error,
    isLoading,
    isRevalidating,
    isError,
    load,
    cancel,
    invalidate,
  };
}
